/**
 * city/buildings/house-parts.js
Các cấu phần chi tiết của nhà phố (Cửa sổ, ban công, chuồng cọp, bạt cuốn, cục nóng...)
 */

import * as THREE from 'three';
import { shade } from '../builder-core.js';

class MethodsHolder {
  _houseBody(house, w, h, d, color, seed, aged) {
    const front = this.texMat(this.getPlasterTexture(color, seed, aged), { roughness: 0.92 });
    const side = this.texMat(this.getConcreteTexture(seed % 7), { roughness: 0.95 });
    const top = this.mat('#78716c', { roughness: 0.95 });
    this.box(house, w, h, d, [side, side, top, top, front, side], 0, h / 2, 0);
    return front;
  }

  _addSign(parent, { x = 0, y, z, w, h, depth = 0.1, ...texOpts }) {
    const tex = this.getSignTexture({ ...texOpts, aspect: w / h });
    const face = this.texMat(tex, {}, true);
    const edge = this.mat(shade(texOpts.bg || '#b91c1c', -0.35), { roughness: 0.6 });
    return this.box(parent, w, h, depth, [edge, edge, edge, edge, face, edge], x, y, z);
  }

  // Cửa sổ có khung, đố kính, bậu, chớp mở 2 bên và song sắt tùy chọn
  _addWindow(parent, o) {
    const g = new THREE.Group();
    g.position.set(o.x || 0, o.y, o.z);
    parent.add(g);
    const ft = o.frameT || 0.07;
    const frame = this.mat(o.frame || '#1f2937', { roughness: 0.6 });
    const cols = o.cols ?? (o.w > 1.2 ? 2 : 1);
    const rows = o.rows ?? 1;

    this.box(g, o.w, o.h, 0.04, this.glassMat(o.glass || 'sky'), 0, 0, 0, null, false);
    this.box(g, o.w + ft * 2, ft, 0.1, frame, 0, o.h / 2 + ft / 2, 0.03);
    this.box(g, o.w + ft * 2, ft, 0.1, frame, 0, -o.h / 2 - ft / 2, 0.03);
    [-1, 1].forEach((s) => this.box(g, ft, o.h, 0.1, frame, s * (o.w / 2 + ft / 2), 0, 0.03));
    for (let i = 1; i < cols; i++)
      this.box(g, 0.035, o.h, 0.06, frame, -o.w / 2 + (o.w / cols) * i, 0, 0.03, null, false);
    for (let i = 1; i < rows; i++)
      this.box(g, o.w, 0.03, 0.05, frame, 0, -o.h / 2 + (o.h / rows) * i, 0.03, null, false);

    if (o.sill !== false) {
      this.box(g, o.w + 0.3, 0.07, 0.2, this.mat(o.sillColor || '#e7e5e4'), 0, -o.h / 2 - ft - 0.035, 0.08);
    }
    if (o.hood) {
      this.box(g, o.w + 0.4, 0.07, 0.34, this.mat(o.hood), 0, o.h / 2 + ft + 0.14, 0.15, [0.18, 0, 0]);
    }
    if (o.shutters) {
      const sm = this.texMat(this.getLouverTexture(o.shutters), { roughness: 0.7 });
      const sw = o.w / 2;
      [-1, 1].forEach((s) => this.box(g, sw, o.h, 0.05, sm, s * (o.w / 2 + ft + sw / 2 + 0.02), 0, 0.05));
    }
    if (o.grille) {
      const gm = this.mat(o.grille, { roughness: 0.5, metalness: 0.5 });
      const n = Math.max(3, Math.round(o.w / 0.12));
      for (let i = 1; i < n; i++) this.box(g, 0.022, o.h, 0.022, gm, -o.w / 2 + (o.w / n) * i, 0, 0.1, null, false);
      [-0.5, 0, 0.5].forEach((p) => this.box(g, o.w, 0.03, 0.03, gm, 0, p * o.h * 0.95, 0.1, null, false));
    }
    return g;
  }

  // Ban công: sàn + lan can 'iron' | 'glass' | 'wood' | 'concrete'
  _addBalcony(
    parent,
    { x = 0, y, z, w, depth = 0.8, slabColor = '#d6d3d1', rail = 'iron', railColor = '#1f2937', railH = 0.95 }
  ) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    const slab = this.mat(slabColor, { roughness: 0.85 });
    this.box(g, w, 0.16, depth, slab, 0, -0.08, depth / 2);
    const rm = this.mat(railColor, { roughness: 0.5, metalness: rail === 'iron' ? 0.5 : 0 });
    const fzr = depth - 0.04;

    if (rail === 'glass') {
      const gm = this.mat('#bae6fd', { transparent: true, opacity: 0.35, roughness: 0.05, metalness: 0.2 });
      const gh = railH - 0.08;
      this.box(g, w - 0.04, gh, 0.03, gm, 0, gh / 2, fzr, null, false);
      [-1, 1].forEach((s) =>
        this.box(g, 0.03, gh, depth - 0.06, gm, s * (w / 2 - 0.02), gh / 2, depth / 2, null, false)
      );
      this.box(g, w, 0.05, 0.06, rm, 0, railH, fzr);
      [-1, 1].forEach((s) => this.box(g, 0.06, 0.05, depth, rm, s * (w / 2 - 0.03), railH, depth / 2));
      return g;
    }
    if (rail === 'concrete') {
      this.box(g, w, railH, 0.12, slab, 0, railH / 2, depth - 0.06);
      [-1, 1].forEach((s) => this.box(g, 0.12, railH, depth, slab, s * (w / 2 - 0.06), railH / 2, depth / 2));
      return g;
    }

    const wood = rail === 'wood';
    const post = wood ? 0.055 : 0.024;
    const step = wood ? 0.17 : 0.13;
    this.box(g, w, wood ? 0.08 : 0.045, wood ? 0.1 : 0.045, rm, 0, railH, fzr);
    this.box(g, w, 0.04, 0.04, rm, 0, 0.1, fzr);
    [-1, 1].forEach((s) => {
      this.box(g, 0.045, wood ? 0.08 : 0.045, depth, rm, s * (w / 2 - 0.03), railH, depth / 2);
      this.box(g, 0.04, 0.04, depth, rm, s * (w / 2 - 0.03), 0.1, depth / 2);
    });
    const n = Math.max(2, Math.round(w / step));
    for (let i = 0; i <= n; i++) {
      this.box(
        g,
        post,
        railH - 0.1,
        post,
        rm,
        -w / 2 + 0.03 + (i * (w - 0.06)) / n,
        (railH + 0.1) / 2,
        fzr,
        null,
        false
      );
    }
    const m = Math.max(1, Math.round(depth / step));
    [-1, 1].forEach((s) => {
      for (let j = 0; j < m; j++) {
        this.box(
          g,
          post,
          railH - 0.1,
          post,
          rm,
          s * (w / 2 - 0.03),
          (railH + 0.1) / 2,
          (j + 0.5) * (depth / m),
          null,
          false
        );
      }
    });
    return g;
  }

  // "Chuồng cọp": lồng sắt bao kín ban công, trên có mái tôn nhỏ
  _addIronCage(parent, { x = 0, y, z, w, depth, h, color = '#2f4f3a', roofColor = '#15803d' }) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    const im = this.mat(color, { roughness: 0.55, metalness: 0.5 });
    const fzr = depth - 0.03;
    const n = Math.round(w / 0.15);
    for (let i = 0; i <= n; i++) this.box(g, 0.025, h, 0.025, im, -w / 2 + (w / n) * i, h / 2, fzr, null, false);
    const m = Math.round(depth / 0.15);
    [-1, 1].forEach((s) => {
      for (let j = 0; j < m; j++)
        this.box(g, 0.025, h, 0.025, im, s * (w / 2 - 0.01), h / 2, (j + 0.5) * (depth / m), null, false);
    });
    [0.1, 0.95, 1.75, h].forEach((yy) => {
      this.box(g, w, 0.04, 0.04, im, 0, yy, fzr, null, false);
      [-1, 1].forEach((s) => this.box(g, 0.04, 0.04, depth, im, s * (w / 2 - 0.01), yy, depth / 2, null, false));
    });
    // Họa tiết hoa sắt dải dưới
    for (let i = 0; i < n; i += 2) {
      this.box(g, 0.1, 0.1, 0.02, im, -w / 2 + (w / n) * (i + 1), 0.52, fzr, [0, 0, Math.PI / 4], false);
    }
    const tole = this.texMat(this.getToleTexture(roofColor), { roughness: 0.45, metalness: 0.4 });
    this.box(g, w + 0.16, 0.04, depth + 0.25, tole, 0, h + 0.08, depth / 2 + 0.08, [0.14, 0, 0]);
    return g;
  }

  // Mái bạt sọc cuốn, có diềm rủ lượn sóng phía trước
  _addAwning(
    parent,
    { x = 0, y, z, w, depth = 1.2, drop = 0.45, colorA = '#dc2626', colorB = '#f8fafc', stripes = 7 }
  ) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    const len = Math.hypot(depth, drop);
    const ang = Math.atan2(drop, depth);
    const sw = w / stripes;
    const mA = this.mat(colorA, { roughness: 0.75 });
    const mB = this.mat(colorB, { roughness: 0.75 });
    for (let i = 0; i < stripes; i++) {
      const m = i % 2 ? mB : mA;
      const sx = -w / 2 + (i + 0.5) * sw;
      this.box(g, sw, 0.03, len, m, sx, -drop / 2, depth / 2, [ang, 0, 0]);
      this.box(g, sw, 0.16, 0.02, m, sx, -drop - 0.08, depth + 0.01);
      this.box(g, sw * 0.6, 0.07, 0.02, m, sx, -drop - 0.19, depth + 0.01, null, false);
    }
    const arm = this.mat('#44403c', { metalness: 0.6, roughness: 0.4 });
    [-1, 1].forEach((s) =>
      this.box(g, 0.03, 0.03, len, arm, s * (w / 2 - 0.02), -drop / 2 - 0.03, depth / 2, [ang, 0, 0])
    );
    this.cyl(g, 0.06, 0.06, w, 10, arm, 0, 0.02, 0.07, [0, 0, Math.PI / 2]);
    return g;
  }

  // Cục nóng điều hòa có lồng quạt tròn & giá đỡ
  _addAcUnit(parent, x, y, z) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    this.box(g, 0.7, 0.5, 0.26, this.mat('#e5e7eb', { roughness: 0.5 }), 0, 0, 0);
    this.cyl(
      g,
      0.17,
      0.17,
      0.02,
      16,
      this.mat('#374151', { roughness: 0.6 }),
      -0.12,
      0,
      0.135,
      [Math.PI / 2, 0, 0],
      false
    );
    this.cyl(g, 0.05, 0.05, 0.03, 8, this.mat('#9ca3af'), -0.12, 0, 0.14, [Math.PI / 2, 0, 0], false);
    for (let i = 0; i < 4; i++)
      this.box(g, 0.14, 0.015, 0.01, this.mat('#9ca3af'), 0.2, -0.15 + i * 0.1, 0.135, null, false);
    const bracket = this.mat('#52525b', { metalness: 0.6, roughness: 0.4 });
    [-0.25, 0.25].forEach((bx) => this.box(g, 0.04, 0.04, 0.34, bracket, bx, -0.27, -0.02));
    this.box(g, 0.03, 0.8, 0.03, this.mat('#f5f5f4'), 0.33, -0.55, -0.1, null, false);
    return g;
  }

  // Chậu cây với các cụm lá voxel, có thể rủ xuống (cây cúc tần, trầu bà)
  _addPlanter(parent, x, y, z, w, rnd, drape = false) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    this.box(g, w, 0.24, 0.3, this.mat(rnd() < 0.5 ? '#b45309' : '#e7e5e4', { roughness: 0.9 }), 0, 0.12, 0);
    const greens = ['#15803d', '#16a34a', '#166534', '#22c55e'];
    const count = Math.round(w * 7);
    for (let i = 0; i < count; i++) {
      const s = 0.14 + rnd() * 0.16;
      this.box(
        g,
        s,
        s,
        s,
        this.mat(greens[Math.floor(rnd() * greens.length)], { roughness: 0.85 }),
        (rnd() - 0.5) * w,
        0.28 + rnd() * 0.25,
        (rnd() - 0.5) * 0.25
      );
    }
    if (drape) {
      for (let i = 0; i < Math.round(w * 5); i++) {
        const len = 0.3 + rnd() * 0.7;
        this.box(
          g,
          0.1,
          len,
          0.08,
          this.mat(greens[Math.floor(rnd() * 3)], { roughness: 0.85 }),
          (rnd() - 0.5) * w,
          0.05 - len / 2,
          0.17,
          null,
          false
        );
      }
    }
    return g;
  }

  // Giàn hoa giấy rủ: cụm voxel hồng cánh sen xen lá xanh
  _addBougainvillea(parent, x, y, z, w, h, rnd) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    const pinks = ['#db2777', '#ec4899', '#be185d', '#f472b6'];
    const count = Math.round(w * h * 14);
    for (let i = 0; i < count; i++) {
      const s = 0.12 + rnd() * 0.16;
      const t = rnd();
      const m =
        rnd() < 0.72
          ? this.mat(pinks[Math.floor(rnd() * pinks.length)], { roughness: 0.8 })
          : this.mat('#166534', { roughness: 0.85 });
      // Dày ở trên, thưa dần xuống dưới như hoa rủ
      this.box(g, s, s, s * 0.8, m, (rnd() - 0.5) * w * (1 - t * 0.4), -t * t * h, rnd() * 0.12, null, false);
    }
    return g;
  }

  // Đèn lồng Hội An phát sáng
  _addLantern(parent, x, y, z, color) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    const body = this.mat(color, { emissive: color, emissiveIntensity: 0.6, roughness: 0.6 });
    const cap = this.mat('#3f2a14', { roughness: 0.7 });
    this.box(g, 0.012, 0.3, 0.012, cap, 0, 0.3, 0, null, false);
    this.cyl(g, 0.06, 0.06, 0.04, 8, cap, 0, 0.16, 0);
    this.cyl(g, 0.1, 0.15, 0.07, 10, body, 0, 0.1, 0);
    this.cyl(g, 0.15, 0.15, 0.14, 10, body, 0, 0, 0);
    this.cyl(g, 0.15, 0.1, 0.07, 10, body, 0, -0.1, 0);
    this.cyl(g, 0.06, 0.06, 0.04, 8, cap, 0, -0.155, 0);
    this.box(g, 0.035, 0.16, 0.035, this.mat('#b91c1c'), 0, -0.26, 0, null, false);
    return g;
  }

  // Bồn nước inox nằm ngang trên giá sắt
  _addWaterTank(parent, x, y, z) {
    const g = new THREE.Group();
    g.position.set(x, y, z);
    parent.add(g);
    const steel = this.mat('#52525b', { metalness: 0.6, roughness: 0.4 });
    [-0.45, 0.45].forEach((lx) => [-0.25, 0.25].forEach((lz) => this.box(g, 0.05, 0.45, 0.05, steel, lx, 0.225, lz)));
    this.box(g, 1.0, 0.05, 0.56, steel, 0, 0.45, 0);
    const inox = this.mat('#e2e8f0', { metalness: 0.9, roughness: 0.2 });
    this.cyl(g, 0.36, 0.36, 1.3, 20, inox, 0, 0.84, 0, [0, 0, Math.PI / 2]);
    [-0.66, 0.66].forEach((ex) =>
      this.cyl(g, 0.37, 0.37, 0.04, 20, this.mat('#cbd5e1', { metalness: 0.8, roughness: 0.3 }), ex, 0.84, 0, [
        0,
        0,
        Math.PI / 2
      ])
    );
    this.cyl(g, 0.09, 0.09, 0.08, 12, inox, 0.2, 1.22, 0);
    return g;
  }

  // Bộ bàn ghế nhựa thấp vỉa hè + ly trà đá
  _addStreetSeating(parent, x, z, rnd, withUmbrella = false) {
    const g = new THREE.Group();
    g.position.set(x, 0, z);
    parent.add(g);
    const tableMat = this.mat(rnd() < 0.5 ? '#dc2626' : '#2563eb', { roughness: 0.5 });
    this.box(g, 0.62, 0.04, 0.62, tableMat, 0, 0.46, 0);
    [-1, 1].forEach((sx) =>
      [-1, 1].forEach((sz) => this.box(g, 0.04, 0.44, 0.04, tableMat, sx * 0.26, 0.22, sz * 0.26))
    );
    [
      [0.52, 0],
      [-0.52, 0],
      [0, 0.52],
      [0, -0.52]
    ].forEach(([sx, sz]) => {
      const sm = this.mat(['#dc2626', '#2563eb', '#16a34a', '#f59e0b'][Math.floor(rnd() * 4)], { roughness: 0.5 });
      this.box(g, 0.3, 0.035, 0.3, sm, sx, 0.27, sz);
      [-1, 1].forEach((lx) =>
        [-1, 1].forEach((lz) => this.box(g, 0.03, 0.26, 0.03, sm, sx + lx * 0.12, 0.13, sz + lz * 0.12, null, false))
      );
    });
    const tea = this.mat('#fbbf24', { transparent: true, opacity: 0.8, roughness: 0.1 });
    [
      [-0.12, 0.1],
      [0.14, -0.08],
      [0.05, 0.16]
    ].forEach(([cx, cz]) => this.cyl(g, 0.035, 0.028, 0.1, 8, tea, cx, 0.53, cz, null, false));
    if (withUmbrella) {
      const pole = this.mat('#e7e5e4', { roughness: 0.5 });
      this.box(g, 0.05, 2.35, 0.05, pole, 0, 1.18, 0);
      const cA = this.mat('#15803d', { roughness: 0.7 });
      const cB = this.mat('#f8fafc', { roughness: 0.7 });
      this.box(g, 2.3, 0.07, 2.3, cA, 0, 2.3, 0, [0, Math.PI / 4, 0]);
      this.box(g, 1.6, 0.09, 1.6, cB, 0, 2.38, 0, [0, Math.PI / 4, 0]);
      this.box(g, 0.9, 0.1, 0.9, cA, 0, 2.47, 0, [0, Math.PI / 4, 0]);
      this.box(g, 0.08, 0.14, 0.08, pole, 0, 2.58, 0);
    }
    return g;
  }
}

export function registerHousePartsMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
